// Stage Mail-V2 Composer (2026-07-23): Shared composer core — types + utilities.
//
// Purpose:
//   Phase 1 抽出共享 Mail V2 composer 架构(types / utils 不依赖 React,
//   可被 useMailComposer / MailComposerContent / 3 个外壳共同使用)。
//
// 适用:
//   - MailV2ComposeDialog     (新建邮件 / 编辑现有 draft)
//   - MailV2InlineDraftEditor (Phase 3+)
//   - MailV2InlineReplyEditor (Phase 4+)
//
// 非目的:
//   - 不持有任何 React hook
//   - 不做 API 调用
//   - 不引用任何具体组件
//
// 安全:
//   - 快照比较用 JSON.stringify;字段是 string | null,不会丢精度
//   - 不引用任何 backend-only 字段(ai_generated_body 等内部字段不暴露给快照)

/** Composer 模式 — 决定 initialSnapshot 来源 + 行为差异。 */
export type MailComposerMode =
  | "new"           // 空白新建(没有 existingDraftId)
  | "edit-existing" // 编辑 mail_drafts 中已存在的 draft
  | "reply";        // Reply/ReplyAll/Forward(parentMessageId 有值,to/subject 由调用方填)

/** Composer 表单字段 — 与 mail_drafts 表 + MailDraftUpsertV2 一一对应。 */
export type MailComposerFields = {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  /** HTML 富文本正文(Phase 2 接入)。Phase 1 阶段编辑器仍是 textarea,bodyHtml 始终为空字符串。 */
  bodyHtml: string;
  /** 纯文本正文 — 即使 Phase 2 启用富文本,也始终保存(由 editor.getText() 派生,作为 SMTP fallback)。 */
  bodyText: string;
};

/** SaveStatus — 反映 draft 持久化状态机。 */
export type SaveStatus =
  | "idle"     // 初始 / 切 draft 后 reset
  | "loading"  // 编辑现有 draft: getDraft 载入中
  | "dirty"    // 当前字段 != 最近一次成功保存快照
  | "saving"   // upsertDraft 飞行中
  | "saved"    // 刚 upsertDraft 成功(800ms 后回 dirty 或保持 saved)
  | "error";   // upsertDraft 失败

/** LoadStatus — 区分 getDraft 载入态 vs autosave 态。 */
export type LoadStatus =
  | "idle"
  | "loading"
  | "ready"
  | "error";

/** 单一字段 snapshot — dirty 检测 + 关 dialog confirm 用。 */
export type MailComposerSnapshot = {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  bodyHtml: string;
  bodyText: string;
};

/** 默认字段 — 空白 compose 起点。 */
export const EMPTY_FIELDS: MailComposerFields = {
  to: "",
  cc: "",
  bcc: "",
  subject: "",
  bodyHtml: "",
  bodyText: "",
};

/** 创建快照 — 给 useMailComposer 做 dirty 判断。 */
export function createSnapshot(fields: MailComposerFields): MailComposerSnapshot {
  return {
    to: fields.to ?? "",
    cc: fields.cc ?? "",
    bcc: fields.bcc ?? "",
    subject: fields.subject ?? "",
    bodyHtml: fields.bodyHtml ?? "",
    bodyText: fields.bodyText ?? "",
  };
}

/** 快照相等 — JSON 字符串比较;字段是 string,安全。 */
export function isSameSnapshot(a: MailComposerSnapshot, b: MailComposerSnapshot): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** SaveStatus → 日本語ラベル。 */
export const SAVE_STATUS_LABEL: Record<SaveStatus, string> = {
  idle: "未編集",
  loading: "読込中…",
  dirty: "未保存",
  saving: "保存中…",
  saved: "保存済み",
  error: "保存に失敗しました",
};

/** LoadStatus → 日本語ラベル。 */
export const LOAD_STATUS_LABEL: Record<LoadStatus, string> = {
  idle: "",
  loading: "下書きを読込中…",
  ready: "",
  error: "下書きの読込に失敗しました",
};